/**
 * Data schema 3 — scoring models: the migration step (specs/models.md §9.2, exactly) and the fresh seeds.
 *
 * Everything below runs with `sync_meta.applying = '1'` (the dirty triggers stay silent), so the migration
 * creates no sync traffic of its own. That is correct only because every write to a synced table is either
 * a FIXED row (seed hlc, or MIGRATION_3_HLC) or a pure function of ONE existing register carrying that
 * register's hlc — the same source gives the same derived row on every replica, and a difference in the
 * source is a difference in hlc that the ordinary merge resolves (the server re-sequences its derived rows
 * once, storage/models-upgrade.ts). A derived row is dirty exactly when its source register is.
 * Keep that property: never derive a synced row from several rows, never give one a fresh clock value.
 *
 *   1. DDL (schema/ddl.ts SCHEMA_V3_STATEMENTS + the two new tables' dirty triggers).
 *   2. engine_config 'plan.free' = the new Free limits (activeModels 1, scoringRules 10). Local.
 *   3. Scoring rules, row by row: every best-score / topic-score condition without `model` gets "jev"; an
 *      empty root ("otherwise") becomes best-score[jev] ≥ 0. The row's hlc and dirtiness are unchanged.
 *   4. Fixed rows, INSERT OR IGNORE with the seed hlc: the on-device ladder rows (positions 1000–1002).
 *   5. Model records — on a database created by this run (from 0): the catalogue defaults with the seed hlc
 *      (the recommended set-up). On a migrated one: 'e5-small' switched off (disabled_reason 'user',
 *      MIGRATION_3_HLC); 'jev' = legacyJev(settings 'scoring') carrying that settings row's hlc (or
 *      MIGRATION_3_HLC when there is no row), dirty exactly when that row is — except on a device that has
 *      synced (cursor > 0) whose settings row is dirty: then MIGRATION_3_HLC, not dirty, and its budget and
 *      text length are carried onto the pulled record after the first pull (LEGACY_JEV_CARRY_KEY; M-R6).
 *   6. Scores, row by row: every live message whose score_state is done / failed / pending gets its
 *      message_scores row for 'jev' from its auto register (hlc = auto_hlc, dirty when the auto register is);
 *      then scoring_model = 'jev' wherever it was set (hlc unchanged).
 *   7. PRAGMA user_version = 3 (the runner).
 */

import type { SqlDriver, SqlValue } from '../contracts/storage.js';
import type { MigrationContext } from '../contracts/storage.js';
import { E5_SMALL_MODEL_ID, JEV_MODEL_ID, type ModelCatalogEntry, type ModelOptions, type ModelRun } from '../contracts/models.js';
import { SEED_HLC } from '../kernel/hlc.js';
import { FREE_PLAN_LIMITS } from './config-defaults.js';
import { schemaV3, SCHEMA_V1_STATEMENTS } from './ddl.js';
import { SCORING_LADDER_SEED } from './ladder.js';
import { legacyJev } from './legacy-jev.js';
import { MODEL_CATALOG } from './model-catalog.js';

/**
 * Clock value of the fixed rows migration 3 writes on an EXISTING database: above the seed (an account's
 * migrated rows win over a new device's fresh seeds on its first pull), below every real clock value.
 */
export const MIGRATION_3_HLC = '0000000000000-0001-mig3';

/**
 * `sync_meta` key (local): the budget and text length of an unpushed legacy edit, to carry onto the account's Jev
 * record after the first complete pull (review M-R6; models.md §9.2 step 5).
 */
export const LEGACY_JEV_CARRY_KEY = 'mig3_jev_carry';

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Step 3 for one stored condition (JSON text): name Jev on every score condition without a model; an empty
 * root becomes "Jev scored it". Returns null when nothing changes (the row is then left byte-identical).
 */
export function nameJevInCondition(conditionJson: string): string | null {
  let tree: unknown;
  try {
    tree = JSON.parse(conditionJson);
  } catch {
    return null;
  }
  if (!isRecord(tree) || tree.type !== 'group' || !Array.isArray(tree.children)) return null;
  let changed = false;
  const visit = (node: unknown): void => {
    if (!isRecord(node)) return;
    if (node.type === 'group' && Array.isArray(node.children)) {
      for (const child of node.children) visit(child);
      return;
    }
    if (node.type === 'condition' && (node.field === 'best-score' || node.field === 'topic-score') && node.model === undefined) {
      node.model = JEV_MODEL_ID;
      changed = true;
    }
  };
  if (tree.children.length === 0) {
    tree.children = [{ type: 'condition', field: 'best-score', operator: 'gte', value: 0, model: JEV_MODEL_ID }];
    changed = true;
  } else {
    visit(tree);
  }
  return changed ? JSON.stringify(tree) : null;
}

/** The column values of a model record row (without hlc). */
export interface ModelRowValues {
  id: string;
  name: string;
  icon: string;
  enabled: 0 | 1;
  disabled_reason: 'user' | 'plan' | null;
  position: number;
  run: string;
  options: string;
  created_at: number;
  deleted_at: null;
}

export function modelRowValues(entry: ModelCatalogEntry, over: { enabled?: boolean; disabledReason?: 'user' | 'plan' | null; run?: ModelRun; options?: ModelOptions; position?: number } = {}): ModelRowValues {
  const enabled = over.enabled ?? entry.defaults.enabled;
  return {
    id: entry.id,
    name: entry.name,
    icon: entry.icon,
    enabled: enabled ? 1 : 0,
    disabled_reason: over.disabledReason !== undefined ? over.disabledReason : enabled ? null : 'user',
    position: over.position ?? entry.defaults.position,
    run: JSON.stringify(over.run ?? entry.defaults.run),
    options: JSON.stringify(over.options ?? entry.defaults.options),
    created_at: 0,
    deleted_at: null,
  };
}

const MODEL_COLUMNS = ['id', 'name', 'icon', 'enabled', 'disabled_reason', 'position', 'run', 'options', 'created_at', 'deleted_at', 'hlc'] as const;

function insertModel(db: SqlDriver, row: ModelRowValues, hlc: string): void {
  const values: SqlValue[] = [row.id, row.name, row.icon, row.enabled, row.disabled_reason, row.position, row.run, row.options, row.created_at, row.deleted_at, hlc];
  db.prepare(`INSERT OR IGNORE INTO models (${MODEL_COLUMNS.join(', ')}) VALUES (${MODEL_COLUMNS.map(() => '?').join(', ')})`).run(values);
}

function insertLadderRow(db: SqlDriver, row: (typeof SCORING_LADDER_SEED)[number]): void {
  db.prepare(
    `INSERT OR IGNORE INTO rules (id, kind, name, icon, enabled, position, trigger_folder_id, condition, actions, created_at, hlc)
     VALUES (?, 'scoring', ?, ?, 1, ?, NULL, ?, ?, 0, ?)`,
  ).run([row.id, row.name, row.icon, row.position, JSON.stringify(row.condition), JSON.stringify(row.actions), SEED_HLC]);
}

/** The dirty mark of a register, or null when it is not dirty. */
function dirtyMark(db: SqlDriver, tbl: string, id: string, grp: string): number | null {
  const r = db.prepare('SELECT mark FROM sync_dirty WHERE tbl = ? AND id = ? AND grp = ?').get([tbl, id, grp]);
  return r ? Number(r.mark) : null;
}

function catalogEntryOf(id: string): ModelCatalogEntry {
  const entry = MODEL_CATALOG.entries.find((e) => e.id === id);
  if (!entry) throw new Error(`migration 3: the catalogue has no entry ${id}`);
  return entry;
}

/** Migration 3 (models.md §9.2 steps 1–6; the runner sets user_version). */
export function migrateToV3(db: SqlDriver, context: MigrationContext): void {
  // 1. DDL and the triggers of models / message_scores.
  for (const statement of schemaV3()) db.exec(statement);

  const applyingBefore = db.prepare("SELECT value FROM sync_meta WHERE key = 'applying'").get()?.value ?? '0';
  db.prepare("INSERT OR REPLACE INTO sync_meta (key, value) VALUES ('applying', '1')").run();
  try {
    // 2. The Free plan's limits (local). A fresh database gets them from config-defaults right after.
    db.prepare("UPDATE engine_config SET value = ? WHERE key = 'plan.free'").run([JSON.stringify(FREE_PLAN_LIMITS)]);

    // 3. Scoring rules name their model.
    for (const r of db.prepare("SELECT id, condition FROM rules WHERE kind = 'scoring' ORDER BY id").all()) {
      const next = nameJevInCondition(String(r.condition));
      if (next !== null) db.prepare('UPDATE rules SET condition = ? WHERE id = ?').run([next, String(r.id)]);
    }

    // 4. The on-device ladder rows (fixed, seed hlc; never overwrite a row that exists).
    for (const row of SCORING_LADDER_SEED) if (row.modelId !== JEV_MODEL_ID) insertLadderRow(db, row);

    // 5. Model records.
    const jev = catalogEntryOf(JEV_MODEL_ID);
    const e5 = catalogEntryOf(E5_SMALL_MODEL_ID);
    if (context.from === 0) {
      for (const entry of MODEL_CATALOG.entries) insertModel(db, modelRowValues(entry), SEED_HLC);
    } else {
      insertModel(db, modelRowValues(e5, { enabled: false, disabledReason: 'user' }), MIGRATION_3_HLC);
      const setting = db.prepare("SELECT value, hlc FROM settings WHERE key = 'scoring'").get();
      let stored: unknown;
      try {
        stored = setting ? JSON.parse(String(setting.value)) : undefined;
      } catch {
        stored = undefined;
      }
      const legacy = legacyJev(stored);
      const values = modelRowValues(jev, { enabled: legacy.enabled, disabledReason: legacy.disabledReason, run: legacy.run, options: legacy.options, position: 0 });
      const mark = setting ? dirtyMark(db, 'settings', 'scoring', 'row') : null;
      const synced = Number(db.prepare("SELECT value FROM sync_meta WHERE key = 'cursor'").get()?.value ?? '0') > 0;
      if (mark !== null && synced) {
        // An edit of the legacy group this device never pushed, on a device that syncs (review M-R6): the account's
        // record may meanwhile hold a newer choice made in the updated app (another device's recommended set-up),
        // which a whole row carrying this edit's clock would silently undo. So the row carries MIGRATION_3_HLC (any
        // record the server holds wins on the first pull; until then this device runs with its own values, budget
        // included), and only the edit's cost controls — budget and text length — are carried onto the pulled
        // record after that pull (engine/model-runs.ts carryLegacyJevEdit). The device's whole row stands when the
        // server holds nothing newer than MIGRATION_3_HLC.
        insertModel(db, values, MIGRATION_3_HLC);
        db.prepare('INSERT OR REPLACE INTO sync_meta (key, value) VALUES (?, ?)').run([
          LEGACY_JEV_CARRY_KEY, JSON.stringify({ dailyBudget: legacy.options.dailyBudget, bodyMaxChars: legacy.options.bodyMaxChars }),
        ]);
      } else {
        insertModel(db, values, setting ? String(setting.hlc) : MIGRATION_3_HLC);
        if (mark !== null) db.prepare("INSERT OR REPLACE INTO sync_dirty (tbl, id, grp, mark) VALUES ('models', ?, 'row', ?)").run([JEV_MODEL_ID, mark]);
      }
      // Any other catalogue entry this build knows (none today) starts like the on-device model: off.
      for (const entry of MODEL_CATALOG.entries) {
        if (entry.id === JEV_MODEL_ID || entry.id === E5_SMALL_MODEL_ID) continue;
        insertModel(db, modelRowValues(entry, { enabled: false, disabledReason: 'user' }), MIGRATION_3_HLC);
      }
    }

    // 6. Jev's scores per message, from each message's auto register.
    db.prepare(
      `INSERT OR IGNORE INTO message_scores (message_id, model_id, state, scores, best_score, best_topic_id, scored_at, model_version, requested_at, hlc, deleted_at)
       SELECT id, ?, score_state, scores, best_score, best_topic_id, scored_at, scoring_model, COALESCE(scored_at, received_at), auto_hlc, NULL
       FROM messages WHERE deleted_at IS NULL AND score_state IN ('done', 'failed', 'pending')`,
    ).run([JEV_MODEL_ID]);
    db.prepare(
      `INSERT OR REPLACE INTO sync_dirty (tbl, id, grp, mark)
       SELECT 'message_scores', s.message_id || '|' || s.model_id, 'row', d.mark
       FROM message_scores s JOIN sync_dirty d ON d.tbl = 'messages' AND d.id = s.message_id AND d.grp = 'auto'
       WHERE s.model_id = ?`,
    ).run([JEV_MODEL_ID]);
    db.prepare('UPDATE messages SET scoring_model = ? WHERE scoring_model IS NOT NULL').run([JEV_MODEL_ID]);
  } finally {
    db.prepare("INSERT OR REPLACE INTO sync_meta (key, value) VALUES ('applying', ?)").run([String(applyingBefore)]);
  }
}

const quote = (v: string | number | null): string => (v === null ? 'NULL' : typeof v === 'number' ? String(v) : `'${v.replace(/'/g, "''")}'`);

/**
 * The seed rows of a FRESH data-schema-3 database, as SQL: the built-in folders, the six ladder rows (each
 * naming its model) and the two model records of the recommended set-up, all with the seed hlc. Exactly what
 * migrations 1 → 2 → 3 leave in a new database (a test holds them equal). "Reset workspace" and a resync wipe
 * run these with `applying = '1'`.
 */
export function freshSeedStatements(): string[] {
  const folders = SCHEMA_V1_STATEMENTS.filter((s) => s.startsWith('INSERT INTO folders'));
  const rules = `INSERT INTO rules (id, kind, name, icon, enabled, position, trigger_folder_id, condition, actions, created_at, hlc) VALUES\n${SCORING_LADDER_SEED.map(
    (r) => `  (${[r.id, 'scoring', r.name, r.icon, 1, r.position, null, JSON.stringify(r.condition), JSON.stringify(r.actions), 0, SEED_HLC].map(quote).join(', ')})`,
  ).join(',\n')}`;
  const models = `INSERT INTO models (${MODEL_COLUMNS.join(', ')}) VALUES\n${MODEL_CATALOG.entries.map((entry) => {
    const v = modelRowValues(entry);
    return `  (${[v.id, v.name, v.icon, v.enabled, v.disabled_reason, v.position, v.run, v.options, v.created_at, v.deleted_at, SEED_HLC].map(quote).join(', ')})`;
  }).join(',\n')}`;
  return [...folders, rules, models];
}
